// src/contexts/AuthContext.tsx - Complete working version with fixed admin login and logout

import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { User, Role } from '@/types';
import { supabase } from '@/lib/supabaseClient';

const FALLBACK_ACCOUNTS = [
  { id: 'admin-1', name: 'System Administrator', email: 'admin@vettandventure.com', role: 'admin', analyst_type: null, password: 'Admin@123' },
  { id: 'demo-1', name: 'Demo Founder Account', email: 'demo@founder.com', role: 'founder', analyst_type: null, password: 'demo123' },
  { id: 'demo-business-1', name: 'Demo Business Analyst', email: 'demo.business@vett.com', role: 'analyst', analyst_type: 'business', password: 'demo123' },
  { id: 'demo-problembank-1', name: 'Demo Problem Bank Analyst', email: 'demo.problembank@vett.com', role: 'analyst', analyst_type: 'problem_bank', password: 'demo123' },
  { id: 'demo-senior-1', name: 'Demo Senior Analyst', email: 'demo.senior@vett.com', role: 'senior_analyst', analyst_type: 'senior', password: 'demo123' },
];

export type AnalystType = 'business' | 'problem_bank' | null;

interface ExtendedUser extends Omit<User, 'password'> {
  analyst_type?: AnalystType;
}

interface AuthState {
  user: ExtendedUser | null;
  isLoading: boolean;
  isInitialized: boolean;
  login: (email: string, password: string, role: Role, analystType?: AnalystType) => string | null;
  register: (name: string, email: string, password: string) => string | null;
  logout: () => void;
  allUsers: ExtendedUser[];
  createUser: (userData: { name: string; email: string; password: string; role: Role; analystType?: AnalystType }) => Promise<string | null>;
  deleteUser: (userId: string, currentUserId: string) => Promise<string | null>;
  updateUserRole: (userId: string, newRole: Role, currentUserId: string, currentUserRole: Role) => Promise<string | null>;
  getUserById: (userId: string) => ExtendedUser | undefined;
  refreshUsers: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [users, setUsers] = useState<(User & { analyst_type?: AnalystType })[]>([]);
  const [user, setUser] = useState<ExtendedUser | null>(null);

  // Sync users from database
  const syncFromDatabase = useCallback(async () => {
    try {
      console.log('📊 Syncing users from database...');
      const { data: dbUsers, error } = await supabase
        .from('users')
        .select('*');
      
      if (error) throw error;
      
      if (dbUsers && dbUsers.length > 0) {
        setUsers(dbUsers);
        console.log(`✅ Loaded ${dbUsers.length} users from database`);
        return dbUsers;
      }
    } catch (error) {
      console.error('Error syncing from database:', error);
    }
    
    // Fallback to demo accounts if no database connection
    console.log('📝 Using fallback demo accounts');
    setUsers(FALLBACK_ACCOUNTS);
    return FALLBACK_ACCOUNTS;
  }, []);

  // Load session from sessionStorage
  const loadSession = useCallback(() => {
    try {
      const stored = sessionStorage.getItem('vett_session');
      if (stored) {
        const parsed = JSON.parse(stored);
        console.log('📦 Loaded session from storage:', parsed);
        return parsed;
      }
    } catch (error) {
      console.error('Error loading session:', error);
    }
    return null;
  }, []);

  // Initialize on mount
  useEffect(() => {
    let isMounted = true;
    
    const init = async () => {
      setIsLoading(true);
      try {
        const savedSession = loadSession();
        const syncedUsers = await syncFromDatabase();
        
        if (isMounted) {
          if (savedSession) {
            const sessionUserExists = syncedUsers.some(u => u.id === savedSession.id);
            if (sessionUserExists) {
              const fullUser = syncedUsers.find(u => u.id === savedSession.id);
              if (fullUser) {
                const restoredUser: ExtendedUser = {
                  id: fullUser.id,
                  name: fullUser.name,
                  email: fullUser.email,
                  role: fullUser.role,
                  analyst_type: fullUser.analyst_type,
                };
                setUser(restoredUser);
                console.log('✅ Session restored:', restoredUser.email, 'Role:', restoredUser.role, 'Type:', restoredUser.analyst_type);
              } else {
                setUser(savedSession);
                console.log('✅ Session restored (from saved):', savedSession.email);
              }
            } else {
              sessionStorage.removeItem('vett_session');
              console.log('⚠️ Session user not found, logged out');
            }
          }
          setIsInitialized(true);
        }
      } catch (error) {
        console.error('Initialization error:', error);
        if (isMounted) {
          setIsInitialized(true);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };
    
    init();
    
    return () => {
      isMounted = false;
    };
  }, [syncFromDatabase, loadSession]);

  // Save session to sessionStorage
  useEffect(() => {
    if (user) {
      const sessionUser = {
        id: user.id,
        name: user.name,
        email: user.email,
        role: user.role,
        analyst_type: user.analyst_type,
      };
      sessionStorage.setItem('vett_session', JSON.stringify(sessionUser));
      console.log('💾 Session saved:', user.email, 'Role:', user.role, 'Type:', user.analyst_type);
    }
  }, [user]);

  const login = useCallback((email: string, password: string, role: Role, analystType?: AnalystType): string | null => {
    let found;
    
    console.log('🔐 Login attempt:', { email, role, analystType });
    
    // Handle admin login
    if (role === 'admin') {
      found = users.find(u => 
        u.email.toLowerCase() === email.toLowerCase() && 
        u.role === 'admin'
      );
      
      if (found && found.password === password) {
        const safeUser: ExtendedUser = {
          id: found.id,
          name: found.name,
          email: found.email,
          role: found.role,
          analyst_type: found.analyst_type,
        };
        console.log('🔐 Admin user logged in:', safeUser.email, 'Role:', safeUser.role);
        setUser(safeUser);
        return null;
      }
      return 'Invalid admin credentials.';
    }
    
    // Handle senior analyst login
    if (role === 'senior_analyst') {
      found = users.find(u => 
        u.email.toLowerCase() === email.toLowerCase() && 
        u.role === 'senior_analyst'
      );
      
      if (found && found.password === password) {
        const safeUser: ExtendedUser = {
          id: found.id,
          name: found.name,
          email: found.email,
          role: found.role,
          analyst_type: found.analyst_type,
        };
        console.log('🔐 Senior Analyst logged in:', safeUser.email);
        setUser(safeUser);
        return null;
      }
      return 'Invalid senior analyst credentials.';
    }
    
    // Handle regular analyst login (with analyst_type)
    if (role === 'analyst' && analystType) {
      found = users.find(u => 
        u.email.toLowerCase() === email.toLowerCase() && 
        u.role === role && 
        u.analyst_type === analystType
      );
      
      if (!found) {
        found = users.find(u => 
          u.email.toLowerCase() === email.toLowerCase() && 
          u.role === role
        );
        if (found) {
          console.warn(`⚠️ User found without analyst_type match. Expected: ${analystType}, Actual: ${found.analyst_type}`);
        }
      }
      
      if (found && found.password === password) {
        const safeUser: ExtendedUser = {
          id: found.id,
          name: found.name,
          email: found.email,
          role: found.role,
          analyst_type: found.analyst_type,
        };
        console.log('🔐 Analyst logged in:', safeUser.email, 'Type:', safeUser.analyst_type);
        setUser(safeUser);
        return null;
      }
      
      if (!found) {
        if (analystType === 'business') return 'No Business Analyst account found with these credentials.';
        if (analystType === 'problem_bank') return 'No Problem Bank Analyst account found with these credentials.';
      }
      return 'Invalid password.';
    }
    
    // Handle founder login
    if (role === 'founder') {
      found = users.find(u => 
        u.email.toLowerCase() === email.toLowerCase() && 
        u.role === 'founder'
      );
      
      if (found && found.password === password) {
        const safeUser: ExtendedUser = {
          id: found.id,
          name: found.name,
          email: found.email,
          role: found.role,
          analyst_type: found.analyst_type,
        };
        console.log('🔐 Founder logged in:', safeUser.email);
        setUser(safeUser);
        return null;
      }
      return 'No founder account found. Please register first.';
    }
    
    return 'Invalid account type selected.';
  }, [users]);

  const register = useCallback((name: string, email: string, password: string): string | null => {
    if (!name.trim()) return 'Name is required.';
    if (!email.trim()) return 'Email is required.';
    if (password.length < 6) return 'Password must be at least 6 characters.';
    
    if (users.find(u => u.email.toLowerCase() === email.toLowerCase())) {
      return 'Email already exists.';
    }
    
    const newUser: User & { analyst_type?: AnalystType } = {
      id: `founder-${Date.now()}`,
      name: name.trim(),
      email: email.trim().toLowerCase(),
      role: 'founder',
      analyst_type: null,
      password,
    };
    
    setUsers(prev => [...prev, newUser]);
    const safeUser: ExtendedUser = {
      id: newUser.id,
      name: newUser.name,
      email: newUser.email,
      role: newUser.role,
      analyst_type: newUser.analyst_type,
    };
    setUser(safeUser);
    
    supabase.from('users').upsert({
      id: newUser.id,
      name: newUser.name,
      email: newUser.email,
      role: newUser.role,
      analyst_type: null,
      password: password,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }).then(({ error }) => {
      if (error) console.error('Error syncing to database:', error);
    });
    
    return null;
  }, [users]);

  const createUser = useCallback(async (userData: { name: string; email: string; password: string; role: Role; analystType?: AnalystType }): Promise<string | null> => {
    if (users.find(u => u.email.toLowerCase() === userData.email.toLowerCase())) {
      return 'Email already exists.';
    }
    
    const newUserId = `${userData.role}-${Date.now()}`;
    const newUser: User & { analyst_type?: AnalystType } = {
      id: newUserId,
      name: userData.name,
      email: userData.email.toLowerCase(),
      role: userData.role,
      analyst_type: userData.analystType || null,
      password: userData.password,
    };
    
    setUsers(prev => [...prev, newUser]);
    
    try {
      const { error } = await supabase
        .from('users')
        .upsert({
          id: newUserId,
          name: userData.name,
          email: userData.email.toLowerCase(),
          role: userData.role,
          analyst_type: userData.analystType || null,
          password: userData.password,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }, { onConflict: 'id' });
      
      if (error) console.error('Error syncing to database:', error);
    } catch (error) {
      console.error('Failed to sync user:', error);
    }
    
    return null;
  }, [users]);

  const deleteUser = useCallback(async (userId: string, currentUserId: string): Promise<string | null> => {
    if (userId === currentUserId) return 'You cannot delete your own account.';
    
    const userToDelete = users.find(u => u.id === userId);
    
    // Prevent deletion of admin accounts
    if (userToDelete?.role === 'admin') return 'Admin accounts cannot be deleted.';
    
    // Prevent deletion of the system administrator fallback account
    if (userToDelete?.email === 'admin@vettandventure.com') return 'The system administrator account cannot be deleted.';
    
    // Prevent deletion of last senior analyst
    if (userToDelete?.role === 'senior_analyst') {
      const seniorAnalystCount = users.filter(u => u.role === 'senior_analyst').length;
      if (seniorAnalystCount <= 1) return 'Cannot delete the last senior analyst account.';
    }
    
    setUsers(prev => prev.filter(u => u.id !== userId));
    
    try {
      await supabase.from('users').delete().eq('id', userId);
    } catch (error) {
      console.error('Failed to delete user from database:', error);
    }
    
    return null;
  }, [users]);

  const updateUserRole = useCallback(async (userId: string, newRole: Role, currentUserId: string, currentUserRole: Role): Promise<string | null> => {
    if (userId === currentUserId) return 'You cannot change your own role. Ask another administrator to do this.';
    
    const userToUpdate = users.find(u => u.id === userId);
    
    // Prevent modification of admin accounts
    if (userToUpdate?.role === 'admin') return 'Admin accounts cannot be modified.';
    
    // Prevent modification of system administrator fallback account
    if (userToUpdate?.email === 'admin@vettandventure.com') return 'The system administrator account cannot be modified.';
    
    // Prevent demotion of last senior analyst
    if (userToUpdate?.role === 'senior_analyst' && newRole !== 'senior_analyst') {
      const seniorAnalystCount = users.filter(u => u.role === 'senior_analyst').length;
      if (seniorAnalystCount <= 1) return 'Cannot demote the last senior analyst account.';
    }
    
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
    
    try {
      await supabase
        .from('users')
        .update({ role: newRole, updated_at: new Date().toISOString() })
        .eq('id', userId);
    } catch (error) {
      console.error('Failed to update user role:', error);
    }
    
    return null;
  }, [users]);

  const getUserById = useCallback((userId: string) => {
    const found = users.find(u => u.id === userId);
    if (!found) return undefined;
    const { password: _, ...userWithoutPassword } = found;
    return userWithoutPassword;
  }, [users]);

  const refreshUsers = useCallback(async () => {
    await syncFromDatabase();
  }, [syncFromDatabase]);

  const logout = useCallback(() => {
    const lastUser = user;
    setUser(null);
    sessionStorage.removeItem('vett_session');
    
    // Role-based redirect to correct login page
    if (lastUser?.role === 'founder') {
      window.location.href = '/app/founder-login';
    } else {
      // Admin, senior_analyst, and analyst all go to analyst login
      window.location.href = '/app/analyst-login';
    }
  }, [user]);

  const allUsers = users.map(({ password, ...u }) => u);

  return (
    <AuthContext.Provider value={{ 
      user, 
      isLoading,
      isInitialized,
      login, 
      register, 
      logout, 
      allUsers,
      createUser, 
      deleteUser, 
      updateUserRole, 
      getUserById,
      refreshUsers,
    }}>
      {children}
    </AuthContext.Provider>
  );
}